import { isEmpty } from 'lodash';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { TextField, MenuItem, Button, Box, FormHelperText, Checkbox } from '@mui/material';

import {
  CalculationFunctionValuesEnum,
  fromCalculationFunctionValueToHumanReadable,
  MechanicFormValuesType,
} from 'entities/mechanic';

import { useGetAllSkuListQuery } from 'widgets/mechanicDetailPage/api/getMechanicDetailApi';
import { VirtualSelect } from 'shared/ui/VirtualSelect';
import { MechanicStepTable } from '../MechanicStepTable';

type MechanicFormPropsType = {
  defaultValues: Partial<MechanicFormValuesType>;
  onSave: SubmitHandler<MechanicFormValuesType>;
  mode: 'create' | 'update';
};

export const MechanicForm = ({ defaultValues, onSave, mode }: MechanicFormPropsType) => {
  const saveBtnTextMapper = {
    create: 'Создать',
    update: 'Изменить',
  };

  const {
    control,
    formState: { errors: formErrors, isDirty },
    watch,
    handleSubmit,
    setValue,
    getValues,
  } = useForm<MechanicFormValuesType>({
    mode: 'onChange',
    defaultValues,
  });

  const { data: skuList } = useGetAllSkuListQuery({
    app_type: 'admin',
  });

  const calculationFunctionWatcher = watch('calculation_function');
  const individualCheckboxWatcher = watch('individual');
  const isCycleCheckboxWatcher = watch('is_cycle');
  const isIndividual =
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF1 ||
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF2 ||
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF6;
  const isCycle =
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF1 ||
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF2 ||
    calculationFunctionWatcher === CalculationFunctionValuesEnum.CF4;

  return (
    <>
      <div>
        <div style={{ marginBottom: '20px' }}>Информация о механике</div>
        <Controller
          name="name"
          control={control}
          rules={{ required: { message: 'Поле обязательно для заполнения', value: true } }}
          render={({ field }) => (
            <TextField
              {...field}
              type="text"
              label="Название механики"
              placeholder=""
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                marginBottom: '24px',
              }}
              required
              error={!!formErrors.name}
              helperText={formErrors.name?.message}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              type="text"
              label="Описание механики"
              placeholder=""
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                marginBottom: '24px',
              }}
            />
          )}
        />
        <Controller
          name="is_active"
          control={control}
          rules={{ required: { message: 'Поле обязательно для заполнения', value: true } }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              type="number"
              defaultValue=""
              value={field.value ?? ''}
              label="Статус механики"
              placeholder=""
              fullWidth
              size="small"
              required
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                marginBottom: '24px',
              }}
              error={!!formErrors.is_active}
              helperText={formErrors.is_active?.message}
            >
              <MenuItem value={1}>Активная</MenuItem>
              <MenuItem value={0}>Неактивная</MenuItem>
            </TextField>
          )}
        />
        <Controller
          name="calculation_function"
          control={control}
          rules={{ required: { message: 'Поле обязательно для заполнения', value: true } }}
          render={({ field }) => (
            <TextField
              {...field}
              defaultValue=""
              value={field.value ?? ''}
              select
              label="Функция расчета"
              required
              placeholder=""
              fullWidth
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                marginBottom: '24px',
              }}
              error={!!formErrors.calculation_function}
              helperText={formErrors.calculation_function?.message}
            >
              <MenuItem value={0}>
                {`CF0 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF0)}`}
              </MenuItem>
              <MenuItem value={1}>
                {`CF1 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF1)}`}
              </MenuItem>
              <MenuItem value={2}>
                {`CF2 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF2)}`}
              </MenuItem>
              <MenuItem value={3}>
                {`CF3 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF3)}`}
              </MenuItem>
              <MenuItem value={4}>
                {`CF4 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF4)}`}
              </MenuItem>
              <MenuItem value={5}>
                {`CF5 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF5)}`}
              </MenuItem>
              <MenuItem value={6}>
                {`CF6 - ${fromCalculationFunctionValueToHumanReadable(CalculationFunctionValuesEnum.CF6)}`}
              </MenuItem>
            </TextField>
          )}
        />
        <Controller
          name="skus"
          control={control}
          render={({ field }) => (
            <VirtualSelect
              list={skuList === undefined ? [] : skuList.skus}
              label="SKU"
              onChange={field.onChange}
              required={false}
              getOptionLabel={(option) => option.sku_id}
              rowVisibleFieldName="sku_id"
            />
          )}
        />
        {isIndividual && (
          <Box sx={{ width: '100%' }}>
            <Controller
              control={control}
              name="individual"
              render={({ field: { value, ...restField } }) => (
                <>
                  <Checkbox
                    checked={value}
                    {...restField}
                  />{' '}
                  Индивидуальный для ТТ
                </>
              )}
            />
          </Box>
        )}
        {isCycle && (
          <Box sx={{ width: '100%' }}>
            <Controller
              control={control}
              name="is_cycle"
              render={({ field: { value, ...restField } }) => (
                <>
                  <Checkbox
                    checked={value}
                    {...restField}
                  />{' '}
                  Циклическая механика
                </>
              )}
            />
          </Box>
        )}
        {mode === 'create' && (
          <Controller
            name="steps"
            control={control}
            rules={{
              required: { message: 'Шагов должно быть >= 1', value: true },
            }}
            render={() => (
              <>
                <MechanicStepTable
                  calculationFunction={calculationFunctionWatcher as CalculationFunctionValuesEnum}
                  setValue={setValue}
                  rows={getValues('steps')}
                  isIndividual={individualCheckboxWatcher}
                  isCycle={isCycleCheckboxWatcher}
                />
                <FormHelperText error={!!formErrors.steps && !!formErrors.steps?.message}>
                  {!!formErrors.steps && formErrors.steps?.message}
                </FormHelperText>
              </>
            )}
          />
        )}
      </div>
      <Box
        sx={{
          display: 'flex',
          marginTop: '30px',
        }}
      >
        <Button
          disabled={!isEmpty(formErrors) || !isDirty}
          variant="contained"
          sx={{ textTransform: 'none' }}
          onClick={handleSubmit(onSave)}
        >
          {saveBtnTextMapper[mode] || 'Создать'}
        </Button>
      </Box>
    </>
  );
};
